import { createGqlFetcher } from 'app/lib/gqlFetcher'
import { useSearchParams } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { default as ComboMultiSelectAsync } from 'app/components/ui/combo/combo-multi-select-async'
import { FilterObject, FilterableColumn } from 'app/filter.utils'
import { useImmediateRequest } from 'app/hooks/useImmediateRequest'
import type { UserJson } from 'app/types/entities'
import { SearchResponse } from 'app/services/search'
import { useUser } from 'app/utils'
import { debug, logError } from 'lib/logging'
import { OptionType } from 'types/OptionType'

/**
 * This filter has two sets of data.
 * 1. The list of workspaces to filter by
 * 2. The list of users to display in the dropdown
 * In order to achieve this it needs to return the selected users which have userId (dropdown) and workspaceId (filterby).
 */
export default function AssignedToFilter({
  filter,
  column,
  onSelect,
}: {
  filter: FilterObject
  column: FilterableColumn
  limitToMonth?: boolean
  onSelect: (val: OptionType[]) => void
}) {
  const [searchParams] = useSearchParams()
  const [request, requestError] = useImmediateRequest<SearchResponse<UserJson>>(`/api/users/assigned-to`)
  const [assignedTo, setAssignedTo] = useState<OptionType[]>()
  let userIds: string[] = []
  if (column.secondaryAttribute && searchParams.has(column.secondaryAttribute)) {
    userIds = searchParams.get(column.secondaryAttribute)!.split(`,`)
  }
  debug(`assignedTo`, `userIds`, userIds)
  const currentUser = useUser()

  useEffect(() => {
    if (requestError) {
      logError(`Error response from server`, requestError)
    }
  }, [requestError])

  useEffect(() => {
    if (!currentUser || !request || userIds.length === 0) {
      return
    }
    let assignedTo: OptionType[] = (request.hits || [])
      ?.filter((option) => {
        if (Array.isArray(filter.value)) {
          return filter.value?.find(
            (workspaceId) => option.assignedWorkspaceIds!.includes(workspaceId) && userIds.includes(option.id!),
          )
        } else {
          return option.assignedWorkspaceIds?.includes(filter.value!) && userIds.includes(option.id!)
        }
      })
      ?.map((user) => ({
        id: user.id!,
        value: user.assignedWorkspaceIds,
        name: user.id! === currentUser.id ? `Me` : user.name!,
      }))
    setAssignedTo(assignedTo.length > 0 ? assignedTo : undefined)
  }, [request])

  if (!currentUser) return null

  return (
    <ComboMultiSelectAsync
      fetcher={createGqlFetcher('users')}
      customLabel={
        !!assignedTo?.at(0)
          ? `${assignedTo?.at(0)?.name} ${assignedTo?.length > 1 ? ` (+${assignedTo.length - 1})` : ``}`
          : `All`
      }
      inputClassName="flex !h-8 w-full cursor-pointer items-center justify-between rounded border border-default bg-surface !px-2 !text-body-sm focus:border-subtle focus:outline-none focus:ring-1 focus:ring-strong"
      name="assigned-to-combo"
      optionsContainerClassName="max-h-[480px]"
      values={assignedTo ? assignedTo.map((a) => a.name) : undefined}
      onChangeHandler={(selectedOptions) => {
        let assignedTo: OptionType[] = []
        if (selectedOptions == null || selectedOptions.length === 0) {
          assignedTo = [{ id: ``, name: `All`, value: `` }]
        } else {
          assignedTo = selectedOptions
        }
        setAssignedTo(assignedTo)
        onSelect(assignedTo.map((a) => ({ ...a, value: a.value.split(`,`) })))
      }}
      optionsMapper={(res) => {
        let options = res?.hits?.map((user) => ({
          id: user.id!,
          value: user.assignedWorkspaceIds.join(`,`),
          name: user.id === currentUser.id ? `Me` : user.name!,
        }))
        return options
      }}
      disabled={column.readOnly}
      enableDeselectAllOption
    />
  )
}
